import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavbarClass {
  backgroundColor: string;
  padding: string;
  textColor: string;
  borderColor: string;
}

const Navbar: React.FC = () => {
  const [navbarClass, setNavbarClass] = useState<NavbarClass>({
    backgroundColor: "bg-transparent",
    padding: "py-3",
    textColor: "text-[#111010]",
    borderColor: "bg-[#111010]",
  });

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  const location = useLocation(); // Hook to get the current path

  useEffect(() => {
    const updateNavbarOnScroll = () => {
      const scrolled = window.scrollY >= 80 || menuOpen;
      setIsScrolled(scrolled);

      if (scrolled) {
        setNavbarClass({
          backgroundColor: "bg-[#111010]",
          padding: "py-1 md:py-0",
          textColor: "text-[#dbdbda]",
          borderColor: "bg-white",
        });
      } else {
        // If not scrolled, apply the logic for checking paths
        if (
          location.pathname === "/services" ||
          location.pathname === "/work"
        ) {
          setNavbarClass({
            backgroundColor: "bg-transparent",
            padding: "py-3",
            textColor: "text-white",
            borderColor: "bg-white",
          });
        } else {
          setNavbarClass({
            backgroundColor: "bg-transparent",
            padding: "py-3",
            textColor: "text-[#111010]",
            borderColor: "bg-[#111010]",
          });
        }
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", updateNavbarOnScroll);
    // Run the function initially to set the correct navbar on page load
    updateNavbarOnScroll();

    // Handle clicking outside of the menu to close it
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        if (menuOpen) {
          setTimeout(() => setMenuOpen(false), 100);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", updateNavbarOnScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen, location.pathname]); // Now the effect runs when `menuOpen` or `location.pathname` changes

  // UseEffect for handling navbar class when menu is open
  useEffect(() => {
    if (menuOpen) {
      setNavbarClass({
        backgroundColor: "bg-[#111010]",
        padding: "py-1 md:py-0",
        textColor: "text-white",
        borderColor: "bg-white",
      });
    }
  }, [menuOpen]);

  return (
    <nav
      className={`${navbarClass.backgroundColor} fixed w-full z-40 top-0 left-0 animate-fadeInTopToBottom transition-all duration-300 ${navbarClass.padding}`}
    >
      <div className="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto">
        <Link to="/" onClick={() => setMenuOpen(false)}>
          <h1
            className={`text-2xl relative font-semibold px-2 z-50 left-2 ${navbarClass.textColor}`}
          >
            Pals
          </h1>
        </Link>

        <button
          onClick={(event) => {
            event.stopPropagation();
            setMenuOpen((prev) => !prev);
          }}
          className={`inline-flex items-center z-50 p-2 text-sm  rounded-lg md:hidden focus:outline-none ${navbarClass.textColor}`}
          aria-controls="navbar-sticky"
          aria-expanded={menuOpen}
        >
          <div className="relative w-6 h-6">
            {/* Close Icon */}
            <svg
              className={`absolute top-0 left-0 w-6 h-6 transition-all duration-300 ease-in-out ${
                menuOpen ? "opacity-100 rotate-0" : "opacity-0 -rotate-45"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>

            {/* Menu Icon */}
            <svg
              className={`absolute top-0 left-0 w-6 h-6 transition-all duration-300 ease-in-out ${
                menuOpen ? "opacity-0 rotate-45" : "opacity-100 rotate-0"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </div>
        </button>

        <div
          ref={menuRef}
          className={`${
            menuOpen ? "fixed top-0 z-30 animate-slideDown" : "hidden"
          } w-full md:flex md:w-auto md:order-1 transition-all duration-100`}
          id="navbar-sticky"
        >
          <ul
            className={`flex flex-col p-4 pt-[7vh] md:pt-4 rounded-b-xl relative z-30 ${navbarClass.backgroundColor} md:flex-row md:space-x-8 md:mt-0 md:text-sm md:font-medium md:bg-transparent md:border-0`}
          >
            {["Home", "Work", "Services", "Contact", "About"].map((item) => (
              <li
                key={item.toLowerCase()}
                className={`group relative text-lg md:text-sm font-medium cursor-pointer text-center ${navbarClass.textColor}  transition duration-300`}
              >
                <Link
                  to={`/${
                    item.toLowerCase() === "home" ? "" : `${item.toLowerCase()}`
                  }`}
                  onClick={() => setMenuOpen(false)}
                  className="relative z-10"
                >
                  {item}
                </Link>
                {/* Underline animation */}
                <span
                  className={`absolute left-0 top-5 bottom-0 w-0 h-[1.4px] ${navbarClass.borderColor} transition-all duration-300 ease-in-out group-hover:w-full hidden  md:inline-block`}
                  aria-hidden="true"
                ></span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {menuOpen && (
        <div className="fixed inset-0 backdrop-blur-md z-0 animate-fadeInMedium transition-opacity duration-300"></div>
      )}
    </nav>
  );
};

export default Navbar;
